import { AlertTriangle, Clock, Mail01, RefreshCcw01 } from "@untitledui/icons";
import { FileIcon } from "@untitledui/file-icons";
import { motion, useReducedMotion, useScroll, useTransform, type MotionValue } from "motion/react";
import { useRef, type ComponentType, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

type ProblemStepKey = "fragmented" | "late" | "manual";
type StepSide = "left" | "right";

type ProblemStep = {
    key: ProblemStepKey;
    time: string;
    icon: ComponentType<{ className?: string }>;
    side: StepSide;
    floatingIcons?: Array<{
        fileType: "xlsx" | "pdf" | "csv";
        delay: number;
        yOffset: number;
        positionClass: string;
    }>;
};

const problemSteps: ProblemStep[] = [
    {
        key: "fragmented",
        time: "09:30 AM",
        icon: Mail01,
        side: "left",
        floatingIcons: [
            { fileType: "xlsx", delay: 0.1, yOffset: -18, positionClass: "-bottom-1 left-6" },
            { fileType: "pdf", delay: 0.25, yOffset: 12, positionClass: "bottom-2 left-1/2 -translate-x-1/2" },
            { fileType: "csv", delay: 0.4, yOffset: -14, positionClass: "-bottom-1 right-6" },
        ],
    },
    {
        key: "late",
        time: "01:15 PM",
        icon: Clock,
        side: "right",
    },
    {
        key: "manual",
        time: "04:45 PM",
        icon: RefreshCcw01,
        side: "left",
    },
];

function FloatingIconLayer({
    fileType,
    delay,
    yOffset,
    positionClass,
    scrollYProgress,
    isReducedMotion,
}: {
    fileType: "xlsx" | "pdf" | "csv";
    delay: number;
    yOffset: number;
    positionClass: string;
    scrollYProgress: MotionValue<number>;
    isReducedMotion: boolean;
}) {
    const yParallax = useTransform(
        scrollYProgress,
        [0, 1],
        [isReducedMotion ? 0 : -yOffset, isReducedMotion ? 0 : yOffset],
    );

    return (
        <motion.div
            style={{ y: yParallax }}
            transition={{ delay }}
            className={`absolute hidden rounded-lg border border-neutral-700 bg-neutral-900/70 p-2 backdrop-blur-sm sm:block ${positionClass}`}
        >
            <FileIcon type={fileType} className="size-5 text-neutral-100" />
        </motion.div>
    );
}

function ManualWorkList({ isReducedMotion }: { isReducedMotion: boolean }) {
    const tasks = [
        { id: 1, text: 'Copying row 452 from "Shipment_Log_v3.xlsx"...' },
        { id: 2, text: "Validating B/L data against Email thread #89..." },
        { id: 3, text: "WARNING: System desync detected in port data." },
        { id: 4, text: "Checking status: ETA manual update required." },
        { id: 5, text: "Sending manual update to customer: MSCU7654321." },
    ];

    return (
        <div className="relative h-32 w-full overflow-hidden rounded-md border border-neutral-800 bg-neutral-900/50 p-2 font-mono text-[10px] text-neutral-400">
            <motion.div
                animate={isReducedMotion ? undefined : { y: ["0%", "-100%"] }}
                transition={isReducedMotion ? undefined : { repeat: Infinity, duration: 8, ease: "linear" }}
                className="space-y-1.5"
            >
                {[...tasks, ...tasks].map((task, index) => (
                    <div key={`${task.id}-${index}`} className="flex items-start gap-1.5">
                        {task.text.includes("WARNING") ? (
                            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-400" />
                        ) : (
                            <div className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-neutral-600" />
                        )}
                        <span className={task.text.includes("WARNING") ? "text-amber-300" : ""}>{task.text}</span>
                    </div>
                ))}
            </motion.div>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-neutral-900/80 to-transparent" />
        </div>
    );
}

function ProblemCard({
    step,
    isReducedMotion,
    children,
}: {
    step: ProblemStep;
    isReducedMotion: boolean;
    children?: ReactNode;
}) {
    const { t } = useTranslation("pages:landing");
    const Icon = step.icon;

    const cardVariants = {
        hidden: { opacity: 0, y: 60, rotate: isReducedMotion ? 0 : step.side === "left" ? -2 : 2 },
        visible: { opacity: 1, y: 0, rotate: 0, transition: { duration: 0.6 } },
    };

    return (
        <motion.div
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.4 }}
            className={`relative mb-10 w-full sm:mb-12 md:mb-20 md:w-[45%] ${step.side === "left" ? "md:mr-auto" : "md:ml-auto"}`}
        >
            <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-4 shadow-2xl backdrop-blur-md sm:p-6">
                <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-amber-500/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                <div className="relative z-10 flex flex-col gap-5">
                    <div className="flex items-start justify-between gap-4">
                        <div className="rounded-xl border border-amber-800 bg-amber-950/50 p-2.5 shadow-inner">
                            <Icon className="h-6 w-6 text-amber-400" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-xl font-semibold tracking-tight text-white">{t(`problem.cards.${step.key}.title`)}</h3>
                        </div>
                        <span className="whitespace-nowrap rounded-full bg-amber-950/50 px-2 py-0.5 font-mono text-xs font-medium text-amber-500">
                            {step.time}
                        </span>
                    </div>

                    <div className="relative flex min-h-[100px] w-full items-center justify-center">{children}</div>

                    <p className="text-sm leading-relaxed text-neutral-300">{t(`problem.cards.${step.key}.description`)}</p>
                </div>
            </div>
        </motion.div>
    );
}

export function LandingProblemSection() {
    const { t } = useTranslation("pages:landing");
    const containerRef = useRef<HTMLElement | null>(null);
    const reducedMotion = !!useReducedMotion();

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"],
    });

    const timelineHeight = useTransform(scrollYProgress, [0.1, 0.8], ["0%", "100%"]);

    return (
        <section id="problem" ref={containerRef} className="relative overflow-hidden bg-neutral-950 py-16 sm:py-24 md:py-32">
            <div className="pointer-events-none absolute left-1/2 top-1/4 h-[800px] w-[800px] -translate-x-1/2 rounded-full bg-amber-950/20 blur-[120px]" />

            <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="mx-auto mb-12 max-w-3xl text-center sm:mb-16 md:mb-24">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="mb-3 inline-block text-sm font-semibold uppercase tracking-wider text-amber-500"
                    >
                        {t("problem.label")}
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="mb-5 text-3xl font-extrabold leading-tight tracking-tighter text-white sm:text-4xl md:mb-6 md:text-5xl"
                    >
                        {t("problem.title")}
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="mx-auto max-w-2xl text-base leading-relaxed text-neutral-300 sm:text-lg"
                    >
                        {t("problem.description")}
                    </motion.p>
                </div>

                <div className="relative mx-auto max-w-5xl px-2 sm:px-4 md:px-0">
                    <div className="absolute bottom-0 left-4 top-0 hidden w-px border-l border-dashed border-amber-900 md:block md:left-1/2 md:-translate-x-1/2" />

                    <motion.div
                        style={{ height: timelineHeight }}
                        className="absolute left-4 top-0 hidden w-px origin-top bg-gradient-to-b from-amber-600 via-amber-400 to-amber-600 shadow-[0_0_10px_1px_rgba(251,191,36,0.3)] md:block md:left-1/2 md:-translate-x-1/2"
                    />

                    <div className="relative space-y-0">
                        {problemSteps.map((step) => (
                            <ProblemCard key={step.key} step={step} isReducedMotion={reducedMotion}>
                                {step.key === "fragmented" && (
                                    <div className="relative w-full pb-8">
                                        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_8.5rem]">
                                            <div className="min-h-28 rounded-lg border border-neutral-700 bg-neutral-900/80 p-3 font-mono text-[11px] leading-relaxed text-neutral-500 shadow-md">
                                            <span>Subject: Shipment delay...</span>
                                            <br />
                                            <span className="text-neutral-600">Attachment: Packing_List.pdf</span>
                                            </div>
                                            <div className="min-h-28 rounded border border-neutral-600 bg-neutral-800 p-2 font-mono text-[11px] leading-relaxed text-neutral-400 shadow-lg">
                                                <span>row 12: Container ID mismatch...</span>
                                            </div>
                                        </div>

                                        {step.floatingIcons?.map(({ fileType, delay, yOffset, positionClass }, idx) => (
                                            <FloatingIconLayer
                                                key={idx}
                                                fileType={fileType}
                                                delay={delay}
                                                yOffset={yOffset}
                                                positionClass={positionClass}
                                                scrollYProgress={scrollYProgress}
                                                isReducedMotion={reducedMotion}
                                            />
                                        ))}
                                    </div>
                                )}

                                {step.key === "late" && (
                                    <div className="relative flex h-full w-full items-center justify-center p-4">
                                        <div className="relative h-24 w-full overflow-hidden rounded-lg border border-neutral-700 bg-gradient-to-b from-neutral-800 to-neutral-900">
                                            <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-red-950/30 to-transparent" />
                                            <div className="p-2 font-mono text-xs text-neutral-600">Profit margin %</div>
                                        </div>

                                        <motion.div
                                            animate={reducedMotion ? undefined : { scale: [1, 1.1, 1], opacity: [0.7, 1, 0.7] }}
                                            transition={reducedMotion ? undefined : { repeat: Infinity, duration: 2, ease: "easeInOut" }}
                                            className="absolute rounded-full border-2 border-amber-400 bg-amber-950/80 p-3 shadow-[0_0_15px_3px_rgba(251,191,36,0.4)]"
                                        >
                                            <AlertTriangle className="h-8 w-8 text-amber-300" />
                                        </motion.div>
                                    </div>
                                )}

                                {step.key === "manual" && <ManualWorkList isReducedMotion={reducedMotion} />}
                            </ProblemCard>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
